import { Fragment, useEffect, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, CheckCircle2, Clock, AlarmClock, Calendar, GitBranchPlus, FileText, ChevronDown, ChevronRight, Layers3, Receipt, ShoppingCart, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/api'
import {
  buildOptimisticQuotationPatch,
  formatQuotationMoney,
  getQuotationActionButtonClass,
  getQuotationBookingMetaClass,
  getQuotationPresetChipClass,
  getQuotationContractMetaClass,
  getQuotationQuickActionClass,
  hasRealBookingReference,
  getQuotationPresetFilterPanelClass,
  getQuotationPresetSurfaceClass,
  OPTIMISTIC_BOOKING_REFERENCE,
  QUOTATION_CARD_CLASS,
  getQuotationPresetCountPillClass,
  QUOTATION_STATUS_BADGE_CLASS,
  restoreQuotationStatusCache,
  SALES_QUOTATION_FILTER_QUERY_PARAMS,
  type QuotationActionKind,
  updateQuotationStatusCache,
} from '@/lib/quotation-sales'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { salesApi, type SalesFollowupTask, type CreateFollowupPayload } from '@/services/sales'
import { quotationsApi } from '@/services/quotations'
import { withServiceInstanceId } from '@/lib/service-instance'

import { AssigneeUserSearch } from '@/components/sales/assignee-user-search'

const ACTIVITY_LABELS: Record<string, string> = {
  site_visit: 'Site Visit',
  call: 'Call',
  meeting: 'Meeting',
  quote_prep: 'Quote Prep',
  site_review: 'Site Review',
  contract: 'Contract',
  general: 'General',
}

const PRIORITY_CLASSES: Record<string, string> = {
  high: 'border-rose-300 bg-rose-50 text-rose-700',
  medium: 'border-amber-300 bg-amber-50 text-amber-700',
  low: 'border-slate-200 bg-slate-50 text-slate-600',
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

type SalesQuoteFilterValue = 'all' | 'with' | 'without'
type SalesBookingFilterValue = 'all' | 'converted' | 'open'

type QuotationCardData = {
  id: string | number
  quotation_number: string
  status: string
  total?: string | number
  valid_until?: string | null
  order_reference?: string | number | null
  effective_contract_document_url?: string | null
  contract_document_name?: string | null
}

function matchesQuoteFilter(quotes: QuotationCardData[], value: SalesQuoteFilterValue) {
  if (value === 'all') return true
  return value === 'with' ? quotes.length > 0 : quotes.length === 0
}

function matchesContractFilter(quotes: QuotationCardData[], value: SalesQuoteFilterValue) {
  if (value === 'all') return true
  const hasContract = quotes.some((quote) => Boolean(quote.effective_contract_document_url))
  return value === 'with' ? hasContract : !hasContract
}

function matchesBookingFilter(quotes: QuotationCardData[], value: SalesBookingFilterValue) {
  if (value === 'all') return true
  const hasBooking = quotes.some((quote) => Boolean(quote.order_reference) || quote.status === 'converted')
  return value === 'converted' ? hasBooking : !hasBooking
}

function buildQuotationCreatePath(
  serviceInstanceId: string | null,
  task: Pick<SalesFollowupTask, 'id' | 'enquiry_id'>,
) {
  const base = withServiceInstanceId('/services/quotations/new', serviceInstanceId)
  const [pathname, search = ''] = base.split('?')
  const params = new URLSearchParams(search)
  params.set('source_followup_id', String(task.id))
  if (task.enquiry_id != null) {
    params.set('enquiry_id', String(task.enquiry_id))
  }
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

function TaskTable({
  tasks,
  onComplete,
  completing,
  onCreateNext,
  quotationMap,
  serviceInstanceId,
  expandedTaskIds,
  onToggleQuotes,
  onAcceptQuotation,
  onConvertQuotation,
  activeQuotationAction,
}: {
  tasks: SalesFollowupTask[]
  onComplete: (id: number) => void
  completing: number | null
  onCreateNext: (task: SalesFollowupTask) => void
  quotationMap: Record<number, QuotationCardData[]>
  serviceInstanceId: string | null
  expandedTaskIds: Record<number, boolean>
  onToggleQuotes: (taskId: number) => void
  onAcceptQuotation: (quotationId: string | number) => void
  onConvertQuotation: (quotationId: string | number) => void
  activeQuotationAction: { id: string | number; action: QuotationActionKind } | null
}) {
  if (tasks.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">No tasks in this bucket.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Activity</TableHead>
          <TableHead>Enquiry</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Due</TableHead>
          <TableHead>Assignee</TableHead>
          <TableHead>Priority</TableHead>
          <TableHead>Quotations</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task) => (
          <Fragment key={task.id}>
            <TableRow>
              <TableCell className="text-sm">{ACTIVITY_LABELS[task.activity_type] ?? task.activity_type}</TableCell>
              <TableCell className="text-sm">
                {task.enquiry_name ? (
                  <span className="inline-flex flex-col gap-0">
                    <span className="font-medium">{task.enquiry_name}</span>
                    {task.enquiry_email && <span className="text-xs text-muted-foreground">{task.enquiry_email}</span>}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="font-medium">{task.title}</TableCell>
              <TableCell className="text-sm">{formatDate(task.due_at)}</TableCell>
              <TableCell className="text-sm">{task.assignee_name || '-'}</TableCell>
              <TableCell>
                <Badge variant="outline" className={PRIORITY_CLASSES[task.priority] ?? ''}>
                  {task.priority}
                </Badge>
              </TableCell>
              <TableCell>
                {(quotationMap[task.id] ?? []).length > 0 ? (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-left text-xs font-medium text-primary hover:underline"
                      onClick={() => onToggleQuotes(task.id)}
                    >
                      {expandedTaskIds[task.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {(quotationMap[task.id] ?? []).length} quotation{(quotationMap[task.id] ?? []).length !== 1 ? 's' : ''}
                    </button>
                    {quotationMap[task.id].slice(0, expandedTaskIds[task.id] ? undefined : 2).map((quotation) => (
                      <Link
                        key={String(quotation.id)}
                        to={withServiceInstanceId(`/services/quotations/${quotation.id}`, serviceInstanceId)}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        {quotation.quotation_number} · {quotation.status}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">None</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="outline" className={getQuotationQuickActionClass('open')} asChild>
                    <Link to={buildQuotationCreatePath(serviceInstanceId, task)}>
                      <FileText className="mr-1 h-3.5 w-3.5" />
                      Quote
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={getQuotationQuickActionClass('open')}
                    onClick={() => onCreateNext(task)}
                  >
                    <GitBranchPlus className="mr-1 h-3.5 w-3.5" />
                    Next
                  </Button>
                  {task.status === 'open' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={getQuotationQuickActionClass('ready')}
                      onClick={() => onComplete(task.id)}
                      disabled={completing === task.id}
                    >
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      {completing === task.id ? 'Completing...' : 'Complete'}
                    </Button>
                  )}
                  {task.status !== 'open' && (
                    <span className="text-xs text-muted-foreground capitalize">{task.status}</span>
                  )}
                </div>
              </TableCell>
            </TableRow>
            {expandedTaskIds[task.id] && (quotationMap[task.id] ?? []).length > 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="bg-muted/20">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {quotationMap[task.id].map((quotation) => (
                      <div key={String(quotation.id)} className={`rounded-xl border bg-background/95 p-4 shadow-sm backdrop-blur-[1px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${QUOTATION_CARD_CLASS[quotation.status] ?? 'border-slate-200'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <Link to={withServiceInstanceId(`/services/quotations/${quotation.id}`, serviceInstanceId)} className="text-sm font-semibold tracking-tight text-primary hover:underline">
                            {quotation.quotation_number}
                          </Link>
                          <Badge variant="outline" className={QUOTATION_STATUS_BADGE_CLASS[quotation.status] ?? 'border-slate-200 text-slate-700'}>{quotation.status}</Badge>
                        </div>
                        <div className="mt-4 space-y-2.5 text-sm text-muted-foreground">
                          <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2.5 shadow-sm">
                            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Total</div>
                            <div className="font-medium text-foreground">{formatQuotationMoney(quotation.total)}</div>
                          </div>
                          <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2.5 shadow-sm">
                            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Valid until</div>
                            <div className="font-medium text-foreground">{quotation.valid_until || '-'}</div>
                          </div>
                          <div className={`rounded-lg border px-3 py-2.5 shadow-sm ${getQuotationBookingMetaClass(quotation.order_reference)}`}>
                            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Booking</div>
                            <div>
                              {quotation.order_reference === OPTIMISTIC_BOOKING_REFERENCE ? (
                                <Badge variant="secondary">Creating booking</Badge>
                              ) : hasRealBookingReference(quotation.order_reference) ? (
                                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                  {quotation.order_reference}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                                  {quotation.order_reference || 'Not booked'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`rounded-lg border px-3 py-2.5 shadow-sm ${getQuotationContractMetaClass(Boolean(quotation.effective_contract_document_url))}`}>
                            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Contract</div>
                            <div>
                              {quotation.effective_contract_document_url ? (
                                <a href={quotation.effective_contract_document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-sky-600" />
                                  {quotation.contract_document_name || 'Attached'}
                                </a>
                              ) : (
                                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                                  Missing
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {(quotation.status === 'draft' || quotation.status === 'sent') ? (
                            <Button size="sm" variant="outline" className={getQuotationActionButtonClass('accept')} disabled={activeQuotationAction?.id === quotation.id} onClick={() => onAcceptQuotation(quotation.id)}>
                              {activeQuotationAction?.id === quotation.id && activeQuotationAction.action === 'accept' ? 'Accepting...' : 'Accept'}
                            </Button>
                          ) : null}
                          {quotation.status === 'accepted' ? (
                            <Button size="sm" className={getQuotationActionButtonClass('convert')} disabled={activeQuotationAction?.id === quotation.id} onClick={() => onConvertQuotation(quotation.id)}>
                              {activeQuotationAction?.id === quotation.id && activeQuotationAction.action === 'convert' ? 'Converting...' : 'Convert'}
                            </Button>
                          ) : null}
                          {quotation.effective_contract_document_url ? (
                            <Button size="sm" variant="outline" className={getQuotationActionButtonClass('contract')} asChild>
                              <a href={quotation.effective_contract_document_url} target="_blank" rel="noreferrer">Open Contract</a>
                            </Button>
                          ) : null}
                          {hasRealBookingReference(quotation.order_reference) ? (
                            <Button size="sm" variant="outline" className={getQuotationActionButtonClass('booking')} asChild>
                              <Link to={withServiceInstanceId(`/services/orders/${quotation.order_reference}`, serviceInstanceId)}>Open Booking</Link>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  )
}

export default function SalesFollowUpsPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const queryClient = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [completing, setCompleting] = useState<number | null>(null)
  const [sourceTask, setSourceTask] = useState<SalesFollowupTask | null>(null)
  const [expandedTaskIds, setExpandedTaskIds] = useState<Record<number, boolean>>({})
  const [quoteFilter, setQuoteFilter] = useState<SalesQuoteFilterValue>(() => {
    const value = searchParams.get(SALES_QUOTATION_FILTER_QUERY_PARAMS.quoteScope)
    return value === 'with' || value === 'without' ? value : 'all'
  })
  const [contractFilter, setContractFilter] = useState<SalesQuoteFilterValue>(() => {
    const value = searchParams.get(SALES_QUOTATION_FILTER_QUERY_PARAMS.contractScope)
    return value === 'with' || value === 'without' ? value : 'all'
  })
  const [bookingFilter, setBookingFilter] = useState<SalesBookingFilterValue>(() => {
    const value = searchParams.get(SALES_QUOTATION_FILTER_QUERY_PARAMS.bookingScope)
    return value === 'converted' || value === 'open' ? value : 'all'
  })
  const [activeQuotationAction, setActiveQuotationAction] = useState<{ id: string | number; action: QuotationActionKind } | null>(null)
  const [form, setForm] = useState<CreateFollowupPayload>({
    title: '',
    activity_type: 'general',
    priority: 'medium',
    due_at: '',
    assignee_user_id: null,
    assignee_name: '',
    notes: '',
  })
  const [formError, setFormError] = useState('')

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams)
    const assignParam = (key: string, value: string) => {
      if (value === 'all') {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    }
    assignParam(SALES_QUOTATION_FILTER_QUERY_PARAMS.quoteScope, quoteFilter)
    assignParam(SALES_QUOTATION_FILTER_QUERY_PARAMS.contractScope, contractFilter)
    assignParam(SALES_QUOTATION_FILTER_QUERY_PARAMS.bookingScope, bookingFilter)
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true })
    }
  }, [bookingFilter, contractFilter, quoteFilter, searchParams, setSearchParams])

  const overdueQuery = useQuery({
    queryKey: ['sales', 'followups', 'overdue', serviceInstanceId],
    queryFn: () => salesApi.listFollowups({ service_instance_id: serviceInstanceId, bucket: 'overdue' }),
  })
  const todayQuery = useQuery({
    queryKey: ['sales', 'followups', 'today', serviceInstanceId],
    queryFn: () => salesApi.listFollowups({ service_instance_id: serviceInstanceId, bucket: 'today' }),
  })
  const upcomingQuery = useQuery({
    queryKey: ['sales', 'followups', 'upcoming', serviceInstanceId],
    queryFn: () => salesApi.listFollowups({ service_instance_id: serviceInstanceId, bucket: 'upcoming' }),
  })

  const quotationsQuery = useQuery({
    queryKey: ['sales', 'followup-quotations', serviceInstanceId],
    queryFn: () => quotationsApi.list({ service_instance_id: serviceInstanceId, page_size: 200 }),
  })

  const quotationMap = (quotationsQuery.data?.results ?? []).reduce<Record<number, QuotationCardData[]>>((acc, quotation) => {
    const sourceFollowupId = Number(quotation.source_followup ?? 0)
    if (!sourceFollowupId) return acc
    acc[sourceFollowupId] = acc[sourceFollowupId] || []
    acc[sourceFollowupId].push({
      id: quotation.id,
      quotation_number: quotation.quotation_number,
      status: quotation.status,
      total: quotation.total,
      valid_until: quotation.valid_until,
      order_reference: quotation.order_reference,
      effective_contract_document_url: quotation.effective_contract_document_url || quotation.contract_document_url,
      contract_document_name: quotation.contract_document_name,
    })
    return acc
  }, {})

  const toggleTaskQuotes = (taskId: number) => {
    setExpandedTaskIds((current) => ({ ...current, [taskId]: !current[taskId] }))
  }

  const quotationQueryKeys = [
    ['sales', 'followup-quotations', serviceInstanceId],
    ['commerce', 'quotations', serviceInstanceId],
    ['sales-dashboard', 'quotations', serviceInstanceId],
    ['sales', 'quotations-by-enquiry', serviceInstanceId],
  ] as const

  const invalidateSalesQuotationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'followup-quotations', serviceInstanceId] })
    queryClient.invalidateQueries({ queryKey: ['commerce', 'quotations', serviceInstanceId] })
  }

  const acceptQuotationMutation = useMutation({
    mutationFn: (quotationId: string | number) => quotationsApi.accept(quotationId, serviceInstanceId),
    onMutate: (quotationId) => {
      setActiveQuotationAction({ id: quotationId, action: 'accept' })
      return {
        snapshots: updateQuotationStatusCache(queryClient, quotationId, buildOptimisticQuotationPatch('accept'), quotationQueryKeys),
      }
    },
    onSuccess: () => {
      toast.success('Quotation accepted')
      invalidateSalesQuotationQueries()
    },
    onError: (error, _quotationId, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  const convertQuotationMutation = useMutation({
    mutationFn: (quotationId: string | number) => quotationsApi.convert(quotationId, serviceInstanceId),
    onMutate: (quotationId) => {
      setActiveQuotationAction({ id: quotationId, action: 'convert' })
      return {
        snapshots: updateQuotationStatusCache(queryClient, quotationId, buildOptimisticQuotationPatch('convert'), quotationQueryKeys),
      }
    },
    onSuccess: (result, quotationId) => {
      updateQuotationStatusCache(queryClient, quotationId, {
        status: 'converted',
        converted_at: new Date().toISOString(),
        order_reference: result.order_reference ?? undefined,
        order_id: result.order_id ?? undefined,
        order: result.order_id ?? undefined,
      }, quotationQueryKeys)
      toast.success('Quotation converted to booking')
      invalidateSalesQuotationQueries()
      if (result.order_reference) {
        window.open(withServiceInstanceId(`/services/orders/${result.order_reference}`, serviceInstanceId), '_blank', 'noopener,noreferrer')
      }
    },
    onError: (error, _quotationId, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'followups'] })
  }

  const completeMutation = useMutation({
    mutationFn: (id: number) => salesApi.completeFollowup(id),
    onMutate: (id) => setCompleting(id),
    onSettled: () => { setCompleting(null); invalidate() },
  })

  const createMutation = useMutation({
    mutationFn: (payload: CreateFollowupPayload) => salesApi.createFollowup(payload),
    onSuccess: () => {
      invalidate()
      setShowForm(false)
      setForm({ title: '', activity_type: 'general', priority: 'medium', due_at: '', assignee_user_id: null, assignee_name: '', notes: '' })
      setFormError('')
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const handleSubmit = () => {
    if (!form.title?.trim()) {
      setFormError('Title is required.')
      return
    }
    setFormError('')
    createMutation.mutate({ ...form, service_instance_id: serviceInstanceId, due_at: form.due_at || null })
  }

  const openNextFollowup = (task: SalesFollowupTask) => {
    setSourceTask(task)
    setForm({
      title: `Next follow-up: ${task.title}`,
      activity_type: task.activity_type,
      priority: task.priority,
      due_at: '',
      assignee_user_id: task.assignee_user_id,
      assignee_name: task.assignee_name,
      assignee_email: task.assignee_email,
      notes: task.notes,
      enquiry_id: task.enquiry_id,
      parent_followup_id: task.id,
    })
    setFormError('')
    setShowForm(true)
  }

  const overdue = overdueQuery.data?.results ?? []
  const today = todayQuery.data?.results ?? []
  const upcoming = upcomingQuery.data?.results ?? []
  const filterTasks = (tasks: SalesFollowupTask[]) => tasks.filter((task) => {
    const quotes = quotationMap[task.id] ?? []
    return matchesQuoteFilter(quotes, quoteFilter)
      && matchesContractFilter(quotes, contractFilter)
      && matchesBookingFilter(quotes, bookingFilter)
  })

  const filteredOverdue = filterTasks(overdue)
  const filteredToday = filterTasks(today)
  const filteredUpcoming = filterTasks(upcoming)
  const filteredTotal = filteredOverdue.length + filteredToday.length + filteredUpcoming.length
  const totalTasks = overdue.length + today.length + upcoming.length
  const activePreset = quoteFilter === 'all' && contractFilter === 'all' && bookingFilter === 'all'
    ? 'all'
    : quoteFilter === 'with' && contractFilter === 'without' && bookingFilter === 'all'
      ? 'needs-contract'
      : quoteFilter === 'with' && contractFilter === 'all' && bookingFilter === 'converted'
        ? 'converted'
        : 'open-quotes'

  const applyFilterPreset = (preset: 'all' | 'needs-contract' | 'converted' | 'open-quotes') => {
    if (preset === 'all') {
      setQuoteFilter('all')
      setContractFilter('all')
      setBookingFilter('all')
      return
    }
    if (preset === 'needs-contract') {
      setQuoteFilter('with')
      setContractFilter('without')
      setBookingFilter('all')
      return
    }
    if (preset === 'converted') {
      setQuoteFilter('with')
      setContractFilter('all')
      setBookingFilter('converted')
      return
    }
    setQuoteFilter('with')
    setContractFilter('all')
    setBookingFilter('open')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales Follow Ups</h1>
          <p className="text-sm text-muted-foreground">Manage calls, site visits, and meetings across the sales lifecycle.</p>
        </div>
        <Button variant="outline" className={getQuotationQuickActionClass('open')} onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Follow-up
        </Button>
      </div>

      <Card className={getQuotationPresetSurfaceClass(activePreset)}>
        <CardContent className="pt-6">
          <div className="mb-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('all', activePreset === 'all')} onClick={() => applyFilterPreset('all')}>
              <Layers3 className="h-3.5 w-3.5" />
              <span>All</span>
              <span className={getQuotationPresetCountPillClass('all', activePreset === 'all')}>Flow</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('open-quotes', activePreset === 'open-quotes')} onClick={() => applyFilterPreset('open-quotes')}>
              <FileText className="h-3.5 w-3.5" />
              <span>Open Quotes</span>
              <span className={getQuotationPresetCountPillClass('open-quotes', activePreset === 'open-quotes')}>Pipeline</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('needs-contract', activePreset === 'needs-contract')} onClick={() => applyFilterPreset('needs-contract')}>
              <Receipt className="h-3.5 w-3.5" />
              <span>Needs Contract</span>
              <span className={getQuotationPresetCountPillClass('needs-contract', activePreset === 'needs-contract')}>Paperwork</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('converted', activePreset === 'converted')} onClick={() => applyFilterPreset('converted')}>
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>Converted</span>
              <span className={getQuotationPresetCountPillClass('converted', activePreset === 'converted')}>Won</span>
            </Button>
          </div>
          <div className={`grid gap-3 rounded-lg border p-3 shadow-sm md:grid-cols-3 ${getQuotationPresetFilterPanelClass(activePreset)}`}>
            <div className="space-y-2">
              <Label>Quotation coverage</Label>
              <Select value={quoteFilter} onValueChange={(value: SalesQuoteFilterValue) => setQuoteFilter(value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All follow-ups</SelectItem>
                  <SelectItem value="with">With quotations</SelectItem>
                  <SelectItem value="without">Without quotations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contract coverage</Label>
              <Select value={contractFilter} onValueChange={(value: SalesQuoteFilterValue) => setContractFilter(value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All contract states</SelectItem>
                  <SelectItem value="with">With contract</SelectItem>
                  <SelectItem value="without">Without contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Booking conversion</Label>
              <Select value={bookingFilter} onValueChange={(value: SalesBookingFilterValue) => setBookingFilter(value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All booking states</SelectItem>
                  <SelectItem value="converted">Converted to booking</SelectItem>
                  <SelectItem value="open">Not yet converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 text-sm text-muted-foreground">Showing {filteredTotal} of {totalTasks} follow-ups.</div>
        </CardContent>
      </Card>

      {filteredOverdue.length > 0 && (
        <Card className="border-rose-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlarmClock className="h-4 w-4" />
              Overdue ({filteredOverdue.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TaskTable tasks={filteredOverdue} onComplete={(id) => completeMutation.mutate(id)} completing={completing} onCreateNext={openNextFollowup} quotationMap={quotationMap} serviceInstanceId={serviceInstanceId} expandedTaskIds={expandedTaskIds} onToggleQuotes={toggleTaskQuotes} onAcceptQuotation={(id) => acceptQuotationMutation.mutate(id)} onConvertQuotation={(id) => convertQuotationMutation.mutate(id)} activeQuotationAction={activeQuotationAction} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Today ({todayQuery.isLoading ? '...' : filteredToday.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayQuery.isLoading
            ? <p className="text-sm text-muted-foreground">Loading...</p>
            : <TaskTable tasks={filteredToday} onComplete={(id) => completeMutation.mutate(id)} completing={completing} onCreateNext={openNextFollowup} quotationMap={quotationMap} serviceInstanceId={serviceInstanceId} expandedTaskIds={expandedTaskIds} onToggleQuotes={toggleTaskQuotes} onAcceptQuotation={(id) => acceptQuotationMutation.mutate(id)} onConvertQuotation={(id) => convertQuotationMutation.mutate(id)} activeQuotationAction={activeQuotationAction} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Upcoming ({upcomingQuery.isLoading ? '...' : filteredUpcoming.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingQuery.isLoading
            ? <p className="text-sm text-muted-foreground">Loading...</p>
            : <TaskTable tasks={filteredUpcoming} onComplete={(id) => completeMutation.mutate(id)} completing={completing} onCreateNext={openNextFollowup} quotationMap={quotationMap} serviceInstanceId={serviceInstanceId} expandedTaskIds={expandedTaskIds} onToggleQuotes={toggleTaskQuotes} onAcceptQuotation={(id) => acceptQuotationMutation.mutate(id)} onConvertQuotation={(id) => convertQuotationMutation.mutate(id)} activeQuotationAction={activeQuotationAction} />}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{sourceTask ? 'Create Next Follow-up' : 'Add Follow-up'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {sourceTask && sourceTask.enquiry_id ? (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                Carries enquiry #{sourceTask.enquiry_id} from the source follow-up.
              </div>
            ) : null}
            <div>
              <Label htmlFor="fu-title">Title *</Label>
              <Input id="fu-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Activity Type</Label>
                <Select value={form.activity_type} onValueChange={(v) => setForm((f) => ({ ...f, activity_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTIVITY_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v: 'low' | 'medium' | 'high') => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="fu-due">Due Date / Time</Label>
              <Input id="fu-due" type="datetime-local" value={form.due_at ?? ''} onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="fu-assignee">Assignee</Label>
              <AssigneeUserSearch
                serviceInstanceId={serviceInstanceId}
                value={form.assignee_name ?? ''}
                onSelect={(userId, name, email) => setForm((f) => ({ ...f, assignee_user_id: userId, assignee_name: name, assignee_email: email }))}
                onClear={() => setForm((f) => ({ ...f, assignee_user_id: null, assignee_name: '', assignee_email: '' }))}
              />
            </div>
            <div>
              <Label htmlFor="fu-notes">Notes</Label>
              <Textarea id="fu-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setSourceTask(null) }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
